/*
 * The org's Updates page (docs/api.md, "Updates page"): `GET /orgs/:orgId/updates` lists every update waiting across
 * the person's sites and the org's servers, grouped by what's being updated (a plugin, a theme, WordPress, a PHP
 * release, a Node.js line, Ubuntu's security updates and its others), each with the environments or servers that
 * need it.
 * `POST /orgs/:orgId/updates/install` installs the chosen ones as one run, which the task tray follows (api/bulk.ts).
 * A target being installed says so (`running`), whoever started it, and one whose last try didn't install says that
 * (`failed`). The list is read again when the work on it moves on: the same operations the tray follows.
 */
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import type { ComponentCard } from "./types";
import type { AgentRow } from "./agentUpdates";
import { noteStarted, recentKey } from "./bulk";
import { api } from "./client";
import { keys } from "./keys";
import { useOperations } from "./queries";

export type UpdateGroupType = "plugin" | "theme" | "wordpress" | "php" | "node" | "server";
export const UPDATE_TYPES: UpdateGroupType[] = ["plugin", "theme", "wordpress", "php", "node", "server"];

export type UpdateTarget = {
  siteId: string | null;
  siteName: string | null;
  /** A client's site: its org, for links. */
  orgSlug: string | null;
  environmentId: string | null;
  /** "live", "test", "dev" or an extra environment's slug. */
  envName: string | null;
  envStatus: string | null;
  /** The environment's primary address ("kn-beta.preview.page"), shown quietly by the site's name. */
  address: string | null;
  /** The environment's last check. */
  health: { ok: boolean | null; httpStatus: number | null } | null;
  locked: boolean;
  serverId: string | null;
  serverName: string | null;
  installed: string | null;
  available: string | null;
  /** A new major version. */
  major: boolean;
  /** Fixes a known vulnerability here (Node.js: a security release on the way). */
  security: boolean;
  /** Ubuntu's updates: how many packages. */
  packages: number | null;
  /** Node.js: the line's end date once past it ("2026-04-30"): a note, nothing to install (`available` is null). */
  endOfLife: string | null;
  managedBy: "server" | "git";
  canUpdate: boolean;
  /** Being installed now, or waiting its turn (`running` isn't null). */
  updating: boolean;
  /**
   * What's installing it: its operation (the restore point's, then the update's; null while it waits its turn in a
   * run), queued (not on the server yet, or waiting for the work before it) or running.
   */
  running: { operationId: string | null; state: "queued" | "running"; startedAt: string } | null;
  /** The last try, in the past day, didn't install it: its operation and why. It can be chosen again. */
  failed: { operationId: string; at: string; error: string | null } | null;
  /** Why it can't be updated from here ("Updates by deploying"). */
  reason: string | null;
  /** PHP: the server's other patch releases install with it; Node.js: its end of life, or what the repository asks for. */
  note: string | null;
  /** Hosterr's agent (group "agent"): where its update stands, and what the newer build brings. Absent: an older API. */
  agent?: Pick<AgentRow, "state" | "at" | "when" | "reason" | "unlocks" | "automatic" | "held">;
};

export type UpdateGroup = {
  id: string;
  type: UpdateGroupType;
  slug: string;
  name: string;
  /** The newest on offer; null for Ubuntu's security updates. */
  version: string | null;
  security: boolean;
  /** Plugins and themes: the picture, author and a line about it; null for the others (absent from an older API). */
  card?: ComponentCard | null;
  targets: UpdateTarget[];
};

/**
 * Ubuntu's updates on a server that an install wouldn't take: still being rolled out, not yet to this server
 * (`phased`), or kept back by apt (`keptBack`). Never targets; a quiet row each, under its kind's label. Absent: an older API.
 */
export type ServerHeldBack = { serverId: string; serverName: string; packages: { name: string; from: string; to: string; security: boolean }[] };

export type OrgUpdates = { groups: UpdateGroup[]; counts: Record<UpdateGroupType, number>; canInstall: boolean; phased?: ServerHeldBack[]; keptBack?: ServerHeldBack[] };

/** The Hosterr agent's group. */
export const AGENT_GROUP = "agent";

/**
 * Whether a target counts as waiting (the tabs' counts, the sidebar's badge): an agent that updates by itself in its
 * quiet window, or is updating, is listed with its chip but needs nobody, so it doesn't (the API's countsAsWaiting).
 * A server's card says "Agent update needs you" for the same states (app/AgentState.tsx).
 */
export const AGENT_NEEDS_PERSON: readonly string[] = ["held", "rolled_back", "failed", "stopped", "offline", "manual"];
export const countsAsWaiting = (t: Pick<UpdateTarget, "agent">) => !t.agent || AGENT_NEEDS_PERSON.includes(t.agent.state);

export type Choice = { groupId: string; environmentId?: string; serverId?: string };
/** `title`: the run's, as the tray shows it ("Updating PHP 8.3 on mail-1"). */
export type InstallResult = { bulkId: string | null; title?: string | null; results: (Choice & { status: "queued" | "skipped"; reason?: string })[] };

export const updatesKey = (orgId: string) => ["org", orgId, "updates"] as const;

/** A target's place in its group: its environment or server. */
export const placeOf = (t: UpdateTarget) => t.environmentId ?? t.serverId ?? "";
/** One target's key: its group and place. */
export const choiceKey = (groupId: string, t: UpdateTarget) => `${groupId}|${placeOf(t)}`;

/**
 * The kinds that install what this page lists (a restore point before a site's update is followed through its
 * `followUp`); a deploy rebuilds a Node.js app, on a newer patch or not.
 */
const UPDATE_KINDS = new Set(["site.update", "server.upgrade", "server.php.upgrade", "site.deploy", "agent.update"]);

export function useOrgUpdates() {
  const org = useOrg();
  return useQuery({
    queryKey: updatesKey(org.id),
    queryFn: ({ signal }) => api.get<OrgUpdates>(`/orgs/${org.id}/updates`, signal),
    staleTime: 15_000,
    // A slow fallback while something is being installed; useRefreshOnUpdateWork reads it again as the work moves on.
    refetchInterval: (q) => (q.state.data?.groups.some((g) => g.targets.some((t) => t.updating)) ? 15_000 : false),
  });
}

/**
 * Reads the list (and the sidebar's count) again when update work starts or ends, wherever it was started. The task
 * tray calls it: the org's operations, as the tray reads them (one query, kept live by the event stream and polled
 * while something runs). A moment after the change, so the API has recorded what the operation did (what installed,
 * a server's last upgrade) before it's asked.
 */
export function useRefreshOnUpdateWork() {
  const orgId = useOrg().id;
  const qc = useQueryClient();
  const ops = useOperations({ limit: 100 });
  const sig = (ops.data ?? [])
    .filter((o) => UPDATE_KINDS.has(o.kind) || (o.kind === "backup.create" && o.followUp?.type === "update"))
    .map((o) => `${o.id}:${o.status}`)
    .join(",");
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (!ops.isSuccess) return;
    const before = last.current;
    last.current = sig;
    if (before === null || before === sig) return;
    const t = setTimeout(() => void qc.invalidateQueries({ queryKey: updatesKey(orgId) }), 1_200);
    return () => clearTimeout(t);
  }, [sig, ops.isSuccess, qc, orgId]);
}

/** The sidebar's count: how many updates the page lists, and whether one fixes a known vulnerability. */
export function useUpdatesSummary(enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: [...updatesKey(org.id), "summary"],
    queryFn: ({ signal }) => api.get<{ updates: number; security: boolean }>(`/orgs/${org.id}/updates/summary`, signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Installs the chosen targets; the run goes to the task tray. */
export function useInstallUpdates() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { items: Choice[]; restorePoint: boolean }) => api.post<InstallResult>(`/orgs/${org.id}/updates/install`, v),
    onSuccess: (r) => {
      if (r.bulkId) noteStarted(org.id, r.bulkId);
      void qc.invalidateQueries({ queryKey: updatesKey(org.id) });
      void qc.invalidateQueries({ queryKey: recentKey(org.id) });
      void qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}
